import { api } from '@/shared/api';
import { unwrap, unwrapCollection, type Page } from '@/shared/api/envelope';

import {
    toBillingIntegration,
    toInvoice,
    toInvoiceBatchOutcome,
    toInvoicePdfLink,
    toMonthSummary,
    toOrderInvoice,
    toTariff,
    toTariffServices,
    type BillingIntegration,
    type Invoice,
    type InvoiceBatchOutcome,
    type InvoiceDraftChanges,
    type InvoiceFilters,
    type InvoicePdfLink,
    type InvoiceStatusChange,
    type MonthSummary,
    type NewInvoice,
    type NewTariff,
    type OrderInvoice,
    type Tariff,
    type TariffChanges,
    type TariffService,
} from './model';
import {
    billingIntegrationResourceSchema,
    billingSummaryResourceSchema,
    inspectionTypeResourceSchema,
    invoiceBatchResourceSchema,
    invoicePdfLinkResourceSchema,
    invoiceResourceSchema,
    orderInvoiceResourceSchema,
    tariffResourceSchema,
} from './types';

/** Schrijfacties en tijdelijke links gaan nooit in de offline cache. */
const WRITE = { cacheKey: false } as const;

/**
 * Facturen (`02-api-contract.md` §Facturatie en tegoed). Lezen en uitgeven
 * (versturen) mag alleen `owner`.
 */
export const invoiceApi = {
    /** Nieuwste eerst, gepagineerd. Een concept heeft geen nummer, dus zoeken op nummer vindt het niet. */
    async list(filters: InvoiceFilters = {}): Promise<Page<Invoice>> {
        const page = unwrapCollection(
            invoiceResourceSchema,
            await api.get('/invoices', {
                query: {
                    'filter[status]': filters.status || undefined,
                    'filter[outstanding]': filters.outstanding ? 1 : undefined,
                    'filter[number]': filters.number?.trim() || undefined,
                    page: filters.page,
                    per_page: filters.perPage,
                },
            }),
        );

        return { items: page.items.map(toInvoice), meta: page.meta };
    },

    /**
     * `POST /invoices/batch` - veel facturen in één actie (hoogstens 200), met een
     * uitkomst per factuur. Elke factuur gaat in haar eigen transactie: één fout
     * houdt de rest niet tegen. Alleen de eigenaar mag uitgeven; wie dat niet mag
     * krijgt per factuur `forbidden`.
     */
    async sendBatch(input: {
        invoiceUuids: readonly string[];
        idempotencyKey: string;
    }): Promise<InvoiceBatchOutcome> {
        return toInvoiceBatchOutcome(
            unwrap(
                invoiceBatchResourceSchema,
                await api.post(
                    '/invoices/batch',
                    { invoices: input.invoiceUuids },
                    { ...WRITE, idempotencyKey: input.idempotencyKey },
                ),
            ),
        );
    },

    /**
     * `POST /invoices` - een nieuw concept met eigen regels, voor een klant en
     * eventueel bij een keuring. Het concept krijgt nog geen nummer; dat komt bij
     * versturen.
     */
    async create(input: NewInvoice): Promise<Invoice> {
        return toInvoice(
            unwrap(
                invoiceResourceSchema,
                await api.post(
                    '/invoices',
                    {
                        client_uuid: input.clientUuid,
                        ...(input.orderUuid ? { order_uuid: input.orderUuid } : {}),
                        ...(input.reference ? { reference: input.reference } : {}),
                        lines: input.lines.map((line) => ({
                            description: line.description,
                            quantity: line.quantity,
                            unit_price: line.unitPrice,
                            vat_rate: line.vatRate,
                        })),
                    },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * `GET /invoices/{invoice}` - één factuur mét haar regels en de btw per tarief.
     * De lijst stuurt die niet mee; om een concept te bewerken heb je ze nodig.
     */
    async get(invoiceUuid: string): Promise<Invoice> {
        return toInvoice(unwrap(invoiceResourceSchema, await api.get(`/invoices/${invoiceUuid}`)));
    },

    /**
     * `PATCH /invoices/{invoice}` - een concept wijzigen: omschrijving, aantal,
     * bedrag, btw per regel, extra regels, kenmerk, notitie en betaaltermijn.
     *
     * Wat je niet meestuurt blijft staan; `lines` is wél de hele factuur. Een
     * factuur die al verstuurd is weigert de API met 409 `invoice-not-editable`:
     * die corrigeer je met een creditering en een nieuwe factuur.
     */
    async update(invoiceUuid: string, changes: InvoiceDraftChanges): Promise<Invoice> {
        return toInvoice(
            unwrap(
                invoiceResourceSchema,
                await api.patch(
                    `/invoices/${invoiceUuid}`,
                    {
                        ...(changes.reference === undefined
                            ? {}
                            : { reference: changes.reference }),
                        ...(changes.notes === undefined ? {} : { notes: changes.notes }),
                        ...(changes.paymentTermDays === undefined
                            ? {}
                            : { payment_term_days: changes.paymentTermDays }),
                        ...(changes.lines === undefined
                            ? {}
                            : {
                                  lines: changes.lines.map((line) => ({
                                      description: line.description,
                                      quantity: line.quantity,
                                      unit_price: line.unitPrice,
                                      vat_rate: line.vatRate,
                                  })),
                              }),
                    },
                    WRITE,
                ),
            ),
        );
    },

    /** `POST /invoices/{invoice}/send` - nummeren, dateren en versturen. Alleen `owner`. */
    async send(invoiceUuid: string): Promise<Invoice> {
        return toInvoice(
            unwrap(
                invoiceResourceSchema,
                await api.post(`/invoices/${invoiceUuid}/send`, undefined, WRITE),
            ),
        );
    },

    /**
     * `POST /invoices/{invoice}/status` - betaald, te laat of gecrediteerd. Een
     * stap die niet mag is 409 `invoice-transition-not-allowed`; de reden komt in
     * het logboek van de API.
     */
    async changeStatus(
        invoiceUuid: string,
        input: { status: InvoiceStatusChange; reason?: string },
    ): Promise<Invoice> {
        return toInvoice(
            unwrap(
                invoiceResourceSchema,
                await api.post(
                    `/invoices/${invoiceUuid}/status`,
                    { status: input.status, reason: input.reason || undefined },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * `GET /orders/{order}/invoice` - kan de factuur mee met het rapport van deze
     * keuring, en welke factuur is het. Alleen voor wie facturen ziet (de eigenaar);
     * niet in de offline cache: de dialoog wil weten hoe het nu staat.
     */
    async forOrder(orderUuid: string): Promise<OrderInvoice> {
        return toOrderInvoice(
            unwrap(
                orderInvoiceResourceSchema,
                await api.get(`/orders/${orderUuid}/invoice`, { cacheKey: false }),
            ),
        );
    },

    /**
     * `GET /invoices/{invoice}/pdf` - een kortlevende link naar de PDF. Zolang
     * die er niet is (een concept, of de PDF wordt nog gemaakt) antwoordt de API
     * 404 `invoice-pdf-not-available`.
     */
    async pdfLink(invoiceUuid: string): Promise<InvoicePdfLink> {
        return toInvoicePdfLink(
            unwrap(
                invoicePdfLinkResourceSchema,
                await api.get(`/invoices/${invoiceUuid}/pdf`, WRITE),
            ),
        );
    },
};

/**
 * Tarieven per dienst, alleen voor `owner`: lezen, en publiceren en wijzigen
 * (`tariffs.manage`).
 */
export const tariffApi = {
    /** Alle versies, nieuwste ingangsdatum eerst; het scherm laat verlopen versies weg. */
    async list(): Promise<Tariff[]> {
        return unwrapCollection(
            tariffResourceSchema,
            await api.get('/tariffs', { query: { per_page: 100 } }),
        ).items.map(toTariff);
    },

    /** De diensten van dit bedrijf, voor de keuze bij een nieuw tarief. */
    async services(): Promise<TariffService[]> {
        return toTariffServices(
            unwrapCollection(inspectionTypeResourceSchema, await api.get('/inspection-types'))
                .items,
        );
    },

    /**
     * `POST /tariffs` - een nieuw tarief, ook als het alleen een nieuw bedrag is.
     * Het lopende tarief voor dezelfde dienst stopt de dag voor de ingangsdatum.
     */
    async create(input: NewTariff): Promise<Tariff> {
        return toTariff(
            unwrap(
                tariffResourceSchema,
                await api.post(
                    '/tariffs',
                    {
                        inspection_type_uuid: input.serviceUuid,
                        name: input.name,
                        price: input.price,
                        inspector_fee: input.inspectorFee,
                        vat_rate: input.vatRate,
                        valid_from: input.validFrom || undefined,
                    },
                    WRITE,
                ),
            ),
        );
    },

    /** `PATCH /tariffs/{tariff}` - alleen de naam en de einddatum; bedragen weigert de API. */
    async update(tariffUuid: string, changes: TariffChanges): Promise<Tariff> {
        return toTariff(
            unwrap(
                tariffResourceSchema,
                await api.patch(
                    `/tariffs/${tariffUuid}`,
                    {
                        ...(changes.name === undefined ? {} : { name: changes.name }),
                        ...(changes.validUntil === undefined
                            ? {}
                            : { valid_until: changes.validUntil }),
                    },
                    WRITE,
                ),
            ),
        );
    },
};

export const billingApi = {
    /**
     * `GET /billing/summary` - de maandkaart van de huidige maand, in de tijdzone
     * van het bedrijf. Geteld worden facturen die die maand zijn uitgegeven,
     * zonder concepten en gecrediteerde facturen.
     */
    async monthSummary(): Promise<MonthSummary> {
        return toMonthSummary(
            unwrap(billingSummaryResourceSchema, await api.get('/billing/summary')),
        );
    },

    /** `GET /billing/integration` - de koppeling met het boekhoudpakket, ook als die er niet is. */
    async integration(): Promise<BillingIntegration> {
        return toBillingIntegration(
            unwrap(billingIntegrationResourceSchema, await api.get('/billing/integration')),
        );
    },
};
